import React from 'react';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

export interface ChannelsPageSectionProps {
  /** Nome do ícone (react-icons/fi via shared Icon) */
  icon: IconName;
  /** Título da seção (ex: "Mensagens", "Contas de email") */
  title: string;
  /** Quantidade de itens pra mostrar no pill */
  count: number;
  /** Variante visual — controla cor do título e do count pill */
  variant?: 'default' | 'email';
  /** data-testid do container */
  'data-testid'?: string;
  /** Conteúdo da seção (grid de cards, lista, etc) */
  children: React.ReactNode;
}

const VARIANT_STYLES = {
  default: {
    titleColor: 'var(--theme-muted-text)',
    countBg: 'rgba(127, 127, 127, 0.1)',
    countColor: 'var(--theme-card-text)',
  },
  email: {
    titleColor: '#92400e',
    countBg: '#fef3c7',
    countColor: '#92400e',
  },
} as const;

/**
 * Wrapper visual de seção da página de canais.
 *
 * Renderiza header com ícone + título + count pill + divisor, seguido do conteúdo.
 * Usado pela `ChannelsPage` para separar "Mensagens" e "Contas de email".
 */
export const ChannelsPageSection: React.FC<ChannelsPageSectionProps> = ({
  icon,
  title,
  count,
  variant = 'default',
  'data-testid': testId,
  children,
}) => {
  const styles = VARIANT_STYLES[variant];

  return (
    <section className="mb-8" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        <div
          className="flex items-center gap-2 font-semibold uppercase"
          style={{
            color: styles.titleColor,
            fontSize: 12,
            letterSpacing: '0.04em',
          }}
        >
          <Icon name={icon} className="w-4 h-4" />
          <span>{title}</span>
          <span
            className="rounded-full px-2 py-0.5 normal-case"
            style={{
              backgroundColor: styles.countBg,
              color: styles.countColor,
              fontSize: 11,
              letterSpacing: 0,
              fontWeight: 600,
            }}
          >
            {count}
          </span>
        </div>
        <div
          className="flex-1 h-px"
          style={{ backgroundColor: 'var(--theme-card-border)' }}
        />
      </div>
      {children}
    </section>
  );
};

ChannelsPageSection.displayName = 'ChannelsPageSection';
